

























































import { BFormCheckbox } from "bootstrap-vue";
import Component from "vue-class-component";

import { RingLoader } from "vue-spinners-css";

import _auth from "@/utils/auth";
import { BaseRootPageComponent } from "../BaseRootPageComponent";
import _gateway from "@/utils/gateway";
import { updateGTMGlobalVariables } from "@/utils/gtm";
import Modals from "@/utils/modals";
import { DecryptBiometricSecretStatus, CheckBiometricsStatus } from "@/plugins/native-app";
import * as biometrics from "./biometrics";

export const EmailStorageKey = "email";

@Component({
  components: { BFormCheckbox, RingLoader }
})
export default class SignIn extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  forgotPasswordUrl = "";
  error = "";
  email = this.loadEmail();
  password = "";
  showPassword = false;
  useBiometrics = false;
  loading = false;

  get hasBiometricsHardware() {
    return this.$nativeApp.hasBiometricsHardware;
  }

  get isUsingBiometrics() {
    return this.$nativeApp.isUsingBiometrics;
  }

  async created() {
    this.setTitle("Sign In");
    // updateGTMGlobalVariables(this);

    // If already logged in, redirect to the home page
    if (this.auth.isLoggedIn()) {
      this.$router.push("/");
      return;
    }

    // If the user has biometrics configured and did not just log out, try to login
    const wasLoggedOut = this.$route.query["loggedOut"] === "true";
    if (this.$nativeApp.isUsingBiometrics && !wasLoggedOut) {
      await this.loginWithBiometric();
    }

    try {
      this.loading = true;
      this.forgotPasswordUrl = await this.gateway.getForgotPasswordUrl();
    } catch (err) {
      // Don't need to tell the user we couldn't load the forgot password URL
      console.log(err);
    } finally {
      this.loading = false;
    }
  }

  loadEmail(): string {
    return localStorage.getItem(EmailStorageKey) ?? "";
  }

  saveEmail(email: string) {
    localStorage.setItem(EmailStorageKey, email);
  }

  async onUseBiometricsChanged() {
    // If the user wants to use biometrics, ensure that biometrics are configured on the native device
    if (this.useBiometrics) {
      const result = await this.$nativeApp.checkBiometrics();

      switch (result.status) {
        case CheckBiometricsStatus.CONFIGURED:
          await Modals.biometric.check.showNoSharedDevicesWarning(this);
          break;

        case CheckBiometricsStatus.NO_HARDWARE:
          await Modals.biometric.check.showNoHardware(this);
          break;

        case CheckBiometricsStatus.NOT_CONFIGURED:
          await Modals.biometric.check.showNotConfigured(this);
          break;

        case CheckBiometricsStatus.NO_PERMISSION:
          await Modals.biometric.check.showNoPermission(this);
          break;

        default:
          await Modals.biometric.check.showUnknownError(this);
      }

      // If the native device isn't (or can't be) configured for biometrics, don't enable biometrics
      if (result.status !== CheckBiometricsStatus.CONFIGURED) this.useBiometrics = false;
    }
  }

  async loginWithBiometric() {
    const decryptResult = await this.$nativeApp.decryptBiometricSecret();
    if (decryptResult.status !== DecryptBiometricSecretStatus.SUCCESS) {
      // FYI No modal when status == canceled since the user initiated the cancel

      switch (decryptResult.status) {
        // If the user canceled the operation, no need to show them a modal, just exit
        case DecryptBiometricSecretStatus.CANCELED:
          break;

        case DecryptBiometricSecretStatus.BIOMETRICS_CHANGED:
          await Modals.biometric.login.showBiometricsChanged(this);

          // Since the biometrics are invalid, remove them and auto-select the Use Biometrics checkbox for the user
          this.$nativeApp.removeBiometrics();
          this.useBiometrics = true;
          break;

        case DecryptBiometricSecretStatus.NOT_CONFIGURED:
          await Modals.biometric.login.showBiometricsNotConfigured(this);
          break;

        case DecryptBiometricSecretStatus.NO_PERMISSION:
          await Modals.biometric.login.showNoPermission(this);
          break;

        case DecryptBiometricSecretStatus.FAILED:
          await Modals.biometric.login.showFailed(this);
          break;

        case DecryptBiometricSecretStatus.LOCKED_OUT:
          await Modals.biometric.login.showLockedOut(this);
          break;

        default:
          await Modals.biometric.login.showUnknownError(this);
      }

      return;
    }

    try {
      this.loading = true;
      const result = await this.auth.loginWithBiometrics(decryptResult.email, decryptResult.secret);

      if (result.isSuccess) {
        await this.handleAuthSuccess(result.isPasswordExpiringSoon, result.daysToPasswordExpiration);
      } else if (result.isAccountLocked) {
        // Even though the other errors are being displayed as a modal, use the error alert for the locked account
        // message to match the sign-in button
        this.error =
          "Your account is locked. To unlock, click on the Forgot Password link to reset your password or wait for 24 hours.";
      } else {
        await Modals.biometric.login.showBiometricsNotMatching(this);

        // Since the biometrics are invalid, remove them and auto-select the Use Biometrics checkbox for the user
        this.$nativeApp.removeBiometrics();
        this.useBiometrics = true;
      }
    } catch (e) {
      this.error = "An unexpected error occurred. Either sign in with your email and password or try again.";
    } finally {
      this.loading = false;
    }
  }

  async submit() {
    this.error = "";
    this.loading = true;

    try {
      const email = this.email.trim();
      const password = this.password.trim();
      if (!email || !password) {
        this.error = "Please enter your email and password to sign in.";
        return;
      }

      const trustedDeviceSecret = await this.$nativeApp.getTrustedDeviceSecret(email);
      const result = await this.auth.login(email, password, trustedDeviceSecret);
      if (!result.isSuccess) {
        this.error = result.errorMessage || "An unexpected error occurred";
        return;
      }

      // If the native app is configured for biometrics, but this device no longer has biometrics configured in the
      // backend, then remove the biometrics so that the user can decide to trust the device on the 2FA page. This
      // scenario happens when the trusted device expires or the trusted device is deleted by an admin or from another
      // device.
      if (!result.hasBiometrics && this.$nativeApp.isUsingBiometrics) {
        this.$nativeApp.removeBiometrics();
      }

      // Remember the user's email for next time
      this.saveEmail(email);

      // If the user is fully logged in (because the device was trusted) and they want to use biometrics, setup the
      // biometrics
      if (this.auth.isLoggedIn() && this.useBiometrics) {
        await this.setupBiometrics();
      }

      await this.handleAuthSuccess(result.isPasswordExpiringSoon, result.daysToPasswordExpiration);
    } finally {
      this.loading = false;
    }
  }

  async setupBiometrics() {
    // Even if the setup fails, the user should still continue logging in
    try {
      await biometrics.setup(this, this.gateway);
    } catch (error) {
      await Modals.biometric.setup.showUnknownError(this);
    }
  }

  async handleAuthSuccess(isPasswordExpiring: boolean, daysToPasswordExpiration: number) {
    // If the user is not fully logged in and needs to complete 2FA, send them there. No need to worry about an
    // expiring password if redirecting to 2FA, because they will be prompted after completing 2FA.
    if (!this.auth.isLoggedIn()) {
      this.navigateTo2FAPage();
      return;
    }

    const route = (this.$route.query["redirectTo"] || "/") as string;

    if (isPasswordExpiring) {
      if (daysToPasswordExpiration === 0) {
        await Modals.login.showPasswordExpiringTodayAndNavigateToChangePassword(this, route);
      } else {
        await Modals.login.showPasswordExpiringSoonAndNavigateAway(this, daysToPasswordExpiration, route);
      }
    } else {
      this.$router.push(route);
    }
  }

  async navigateTo2FAPage() {
    const query = {
      redirectTo: this.$route.query["redirectTo"],
      useBiometrics: this.useBiometrics && !this.$nativeApp.isUsingBiometrics ? "true" : undefined
    };

    this.$router.push({ path: "/2fa", query: query });
  }
}
